import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { AnimatePresence } from 'framer-motion';

// Context Providers
import { AuthProvider } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';
import { ThemeProvider } from './context/ThemeContext';

// Layout Components
import Layout from './components/Layout/Layout';
import PrivateRoute from './components/Auth/PrivateRoute';
import PublicRoute from './components/Auth/PublicRoute';

// Auth Pages
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';

// Main Pages
import Dashboard from './pages/Dashboard/Dashboard';
import Documents from './pages/Documents/Documents';
import DocumentDetail from './pages/Documents/DocumentDetail';
import CreateDocument from './pages/Documents/CreateDocument';
import Categories from './pages/Categories/Categories';
import CategoryDetail from './pages/Categories/CategoryDetail';
import Folders from './pages/Folders/Folders';
import FolderDetail from './pages/Folders/FolderDetail';
import Search from './pages/Search/Search';
import Profile from './pages/Profile/Profile';
import Settings from './pages/Settings/Settings';
import Users from './pages/Admin/Users';
import AuditLogs from './pages/Admin/AuditLogs';
import Reports from './pages/Admin/Reports';

// Error Pages
import NotFound from './pages/Errors/NotFound';
import Unauthorized from './pages/Errors/Unauthorized';

// Services
import { initSocket } from './services/socket';

// Styles
import './styles/main.scss';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  const [socket, setSocket] = useState(null);

  useEffect(() => {
    // Initialize socket connection when user is authenticated
    const token = localStorage.getItem('token');
    if (token) {
      const socketInstance = initSocket(token);
      setSocket(socketInstance);

      return () => {
        if (socketInstance) {
          socketInstance.disconnect();
        }
      };
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <Router>
          <AuthProvider>
            <SocketProvider socket={socket}>
              <div className="App">
                <Toaster
                  position="top-right"
                  toastOptions={{
                    duration: 4000,
                    style: {
                      background: '#363636',
                      color: '#fff',
                    },
                    success: {
                      duration: 3000,
                      iconTheme: {
                        primary: '#10B981',
                        secondary: '#fff',
                      },
                    },
                    error: {
                      duration: 4000,
                      iconTheme: {
                        primary: '#EF4444',
                        secondary: '#fff',
                      },
                    },
                  }}
                />
                <AnimatePresence mode="wait">
                  <Routes>
                    {/* Public Routes */}
                    <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
                    <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
                    <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
                    <Route path="/reset-password/:token" element={<PublicRoute><ResetPassword /></PublicRoute>} />

                    {/* Private Routes with Layout */}
                    <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
                      <Route index element={<Navigate to="/dashboard" replace />} />
                      <Route path="dashboard" element={<Dashboard />} />
                      
                      {/* Documents Routes */}
                      <Route path="documents">
                        <Route index element={<Documents />} />
                        <Route path="create" element={<CreateDocument />} />
                        <Route path=":id" element={<DocumentDetail />} />
                      </Route>

                      {/* Categories Routes */}
                      <Route path="categories">
                        <Route index element={<Categories />} />
                        <Route path=":id" element={<CategoryDetail />} />
                      </Route>

                      {/* Folders Routes */}
                      <Route path="folders">
                        <Route index element={<Folders />} />
                        <Route path=":id" element={<FolderDetail />} />
                      </Route>

                      {/* Search */}
                      <Route path="search" element={<Search />} />

                      {/* Profile */}
                      <Route path="profile" element={<Profile />} />

                      {/* Settings */}
                      <Route path="settings" element={<Settings />} />

                      {/* Admin Routes */}
                      <Route path="admin">
                        <Route path="users" element={<Users />} />
                        <Route path="audit-logs" element={<AuditLogs />} />
                        <Route path="reports" element={<Reports />} />
                      </Route>
                    </Route>

                    {/* Error Routes */}
                    <Route path="/unauthorized" element={<Unauthorized />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </AnimatePresence>
              </div>
            </SocketProvider>
          </AuthProvider>
        </Router>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
